import React, { useContext, useState, useEffect } from 'react';
import { Box, Grid, Tab, useMediaQuery, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { PatientTabsContext } from '../../../lib/contexts';
import {
  PatientAssociatedAdminsSelect,
  PatientAlertBox,
  PatientBioBox,
  PatientCheckInHistoryBox,
  PatientKeyNutrientsBox,
  PatientLatestAssessmentsBox,
  PatientRecommendedChallengesBox,
  PatientSummaryBox,
} from './index';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import PatientNotesBox from './PatientNotesBox';
import { usePrint } from '../../../lib/contexts/PrintContext';
import PatientCustomQuestionsBox from './PatientCustomQuestionsBox';
import useMetric from '../../../lib/hooks/useMetric';
import useCustomQuestions from '../../../lib/hooks/useCustomQuestions';
import usePatientRecommendedChallenges from '../../../lib/hooks/usePatientRecommendedChallenges';

const useStyles = makeStyles((theme) => ({
  screen: {
    width: '100%',
    margin: '10px auto',

    flexGrow: 1,
  },
  screenFull: {
    width: '100%',
    margin: '10px auto',
    flexGrow: 1,
  },
  root: {
    flexGrow: 1,
    backgroundColor: theme.palette.admin.activeTab,
    marginTop: -50,
    marginLeft: -50,
    marginRight: -50,
    padding: theme.spacing(2),
    overflowX: 'auto',
  },
  tabPanel: {
    margin: 0,
    padding: 16,
    paddingTop: 0,
    paddingRight: 0,
    width: '100%',
  },
  tabRoot: {
    color: theme.palette.admin.dark,
    borderColor: theme.palette.admin.dark,
  },
  TabTextColor: {
    color: theme.palette.admin.main,
    borderColor: theme.palette.admin.main,
  },
}));

const PatientTabChart = () => {
  const classes = useStyles();
  const [subtab, setSetSubtab] = useState('0');

  const theme = useTheme();
  const matchLG = useMediaQuery(theme.breakpoints.up('lg'));
  const matchXL = useMediaQuery(theme.breakpoints.up('xl'));

  const {
    state: { currentTab },
  } = useContext(PatientTabsContext);

  // unsure what to do with this - we aren't showing a print button on this page
  const { isPrinting, startPrintProcess } = usePrint();

  // Always fetch data for all components to determine tab visibility
  // These hooks will make the API calls once and cache the results
  const {
    customQuestions = [],
    isLoading: questionsIsLoading = false,
    refetch: refetchQuestions,
  } = useCustomQuestions(currentTab?.id) || {};

  const {
    data: challenges = [],
    isLoading: challengesIsLoading = false,
    refetch: refetchChallenges,
  } = usePatientRecommendedChallenges(currentTab?.id) || {};

  const {
    isLoading: checkinHistoryIsLoading,
    data: checkinHistoryData,
    refetch: refetchCheckinHistory,
  } = useMetric('checkin_history', currentTab?.id);

  // Extract the value array from the metric response
  const checkinHistory = checkinHistoryData?.metric?.value || [];

  // Determine if we have data for each tab
  const hasQuestionsData = customQuestions && customQuestions.length > 0;
  const hasChallengesData = challenges && challenges.length > 0;
  const hasCheckinHistoryData = checkinHistory && checkinHistory.length > 0;

  const { isLoading: associatedAdminsIsLoading, data: associatedAdminsData } = useMetric(
    'associated_admins',
    currentTab?.id,
  );

  const a11yProps = (index) => {
    return {
      value: String(index),
      id: `scrollable-tab-${index}`,
      'aria-controls': `scrollable-tabpanel-${index}`,
    };
  };

  const handlePrint = () => {
    const graphElement = document.querySelector('.patient-latest-assessments-box');
    if (graphElement) {
      // Reset any inline styles that might have been applied
      graphElement.style.transform = '';
      graphElement.style.width = '';
      graphElement.style.height = '';
    }

    // Add a class to the body to trigger print-specific styles
    document.body.classList.add('print-patient-tab-chart');
  };

  // Load subtab from localStorage when component mounts or currentTab changes
  useEffect(() => {
    if (currentTab?.id) {
      try {
        // Get saved subtabs from localStorage
        const savedPatientSubtabs = JSON.parse(localStorage.getItem('patientSubtabs') || '{}');
        // Get saved subtab for the current patient, default to '0' (Summary)
        const savedSubtab = savedPatientSubtabs[currentTab.id] || '0';
        setSetSubtab(savedSubtab);
      } catch (error) {
        console.error('Error loading subtab from localStorage:', error);
        setSetSubtab('0'); // Default to Summary tab on error
      }
    }
  }, [currentTab?.id]);

  // Save subtab to localStorage when it changes
  useEffect(() => {
    if (currentTab?.id) {
      try {
        // Get existing saved subtabs or initialize empty object
        const savedPatientSubtabs = JSON.parse(localStorage.getItem('patientSubtabs') || '{}');
        // Update with current selection
        savedPatientSubtabs[currentTab.id] = subtab;
        // Save back to localStorage
        localStorage.setItem('patientSubtabs', JSON.stringify(savedPatientSubtabs));
      } catch (error) {
        console.error('Error saving subtab to localStorage:', error);
      }
    }
  }, [subtab, currentTab?.id]);

  return (
    <Box
      id="patient-tab-chart"
      sx={{ width: '100%', maxWidth: '100%' }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          width: '100%',
          maxWidth: '100%',
          alignItems: 'flex-start',
          mt: 0,
        }}
      >
        {/* PatientBioBox with fixed width */}
        <Box
          sx={{
            width: { xs: '100%', lg: '360px' },
            flexShrink: 0,
            flexGrow: 0,
            boxSizing: 'border-box',
            marginRight: { xs: 0, lg: '24px' },
          }}
        >
          <PatientBioBox />
        </Box>

        {/* Content area that takes remaining space */}
        <Box
          sx={{
            width: { xs: '100%', lg: 'calc(100% - 384px)' }, // 360px for bio + 24px gap
            flexShrink: 1,
            flexGrow: 1,
            boxSizing: 'border-box',
            marginTop: { xs: '24px', lg: 0 },
          }}
        >
          {/* Top row with Alert Box and Associated Admins Select */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'flex-start',
              width: '100%',
              mb: 2,
              mt: 0,
              gap: '24px',
            }}
          >
            {/* Alert box takes full width on small screens, 2/3 on larger screens */}
            <Box
              sx={{
                width: { xs: '100%', md: 'calc(66.67% - 12px)' }, // Full width on small screens, 2/3 on medium+
              }}
            >
              <PatientAlertBox />
            </Box>

            {/* Admin select takes full width on small screens, 1/3 on larger screens */}
            {!associatedAdminsIsLoading && associatedAdminsData && (
              <Box
                sx={{
                  width: { xs: '100%', md: 'calc(33.33% - 12px)' }, // Full width on small screens, 1/3 on medium+
                  display: 'flex',
                  justifyContent: { xs: 'flex-start', md: 'flex-end' }, // Left-aligned on small screens, right-aligned on medium+
                }}
              >
                <PatientAssociatedAdminsSelect data={associatedAdminsData?.metric} />
              </Box>
            )}
          </Box>

          <TabContext value={subtab}>
            <TabList
              value={subtab}
              onChange={(event, value) => setSetSubtab(value)}
              indicatorColor="secondary"
              textColor="secondary"
              variant="scrollable"
              visibleScrollbar={true}
              scrollButtons={false}
              aria-label="patient sub tabs"
              sx={{
                width: '100%',
                maxWidth: '100%',
                marginLeft: 1,
                marginRight: 1,
              }}
            >
              <Tab
                label="Summary"
                {...a11yProps(0)}
              />
              <Tab
                label="Key Nutrients"
                {...a11yProps(1)}
              />
              <Tab
                label="Assessments"
                {...a11yProps(2)}
              />
              {hasQuestionsData && (
                <Tab
                  label="Survey Answers"
                  {...a11yProps(3)}
                />
              )}
              {hasChallengesData && (
                <Tab
                  label="Challenges"
                  {...a11yProps(4)}
                />
              )}
              {hasCheckinHistoryData && (
                <Tab
                  label="Check-In History"
                  {...a11yProps(5)}
                />
              )}
              <Tab
                label="Notes"
                {...a11yProps(6)}
              />
            </TabList>

            <Box
              sx={{
                width: '100%',
                maxWidth: '100%',
                paddingTop: '2px',
              }}
            >
              <TabPanel
                value={'0'}
                className={classes.tabPanel}
              >
                <PatientSummaryBox
                  summary={true}
                  setSetSubtab={setSetSubtab}
                />
                <PatientNotesBox
                  summary={true}
                  setSetSubtab={setSetSubtab}
                />
                <Grid
                  container
                  item
                  xs={12}
                  justifyContent={'space-between'}
                  sx={{ width: '100%', maxWidth: '100%', mx: 0, padding: 0 }}
                >
                  <PatientLatestAssessmentsBox
                    summary={true}
                    setSetSubtab={setSetSubtab}
                  />
                </Grid>
              </TabPanel>
              <TabPanel
                value={'1'}
                className={classes.tabPanel}
              >
                <Grid
                  item
                  xs={12}
                >
                  <PatientKeyNutrientsBox summary={false} />
                </Grid>
              </TabPanel>
              <TabPanel
                value={'2'}
                className={classes.tabPanel}
              >
                <Grid
                  item
                  xs={12}
                >
                  <PatientLatestAssessmentsBox summary={false} />
                </Grid>
              </TabPanel>
              <TabPanel
                value={'6'}
                className={classes.tabPanel}
              >
                <PatientNotesBox />
              </TabPanel>
              {hasChallengesData && (
                <TabPanel
                  value={'4'}
                  className={classes.tabPanel}
                >
                  <PatientRecommendedChallengesBox
                    challenges={challenges}
                    isLoading={challengesIsLoading}
                    refetch={refetchChallenges}
                  />
                </TabPanel>
              )}
              {hasCheckinHistoryData && (
                <TabPanel
                  value={'5'}
                  className={classes.tabPanel}
                >
                  <PatientCheckInHistoryBox
                    checkinHistory={checkinHistory}
                    isLoading={checkinHistoryIsLoading}
                  />
                </TabPanel>
              )}
              {hasQuestionsData && (
                <TabPanel
                  value={'3'}
                  className={classes.tabPanel}
                >
                  <PatientCustomQuestionsBox
                    customQuestions={customQuestions}
                    isLoading={questionsIsLoading}
                  />
                </TabPanel>
              )}
            </Box>
          </TabContext>
        </Box>
      </Box>
    </Box>
  );
};

export default PatientTabChart;
